import { useCallback, useMemo } from "react";
//formik
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { FormikSelectField, FormikTextField } from "src/components/FormikComponents/FormikWrappers";
//material UI
import { Button, CardContent, Grid, Typography, useTheme, Theme, alpha } from "@mui/material";
import { ICanceledFlight, IDelayCategory } from "@vmosolutions/oc-interfaces";

// API calls
import { useDispatch, useSelector } from "react-redux";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { DisplayLastModification } from "src/components/FormikComponents/DisplayLastModification";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IStructuresState } from "src/redux/reducers/StructuresReducer";

import { milli2DaysHrsMin } from "src/utils/index";
import { FormHeader, FormikFormIsDirtyEffect } from "../helpers";

interface ICancelFlightTabProps {
  canceledFlight: ICanceledFlight; // Id of the flight to retrieve and edit if provided.
  setLoadedCanceledFlight: (eventValues: ICanceledFlight) => void;
  updateTabState: (dirtyStatus: boolean) => void; // update parent with dirty status.
  mosaicId: string;
  setAPICallStatus: (statusMessage: string) => void;
  // renderDialogModal: (title: string, message: string) => void;
}
interface ICancelFlightObject {
  canceled: boolean;
  canceledBy: string;
  canceledAt: number;
  reason: string;
  description: string;
}
//set initial value when user click "new flightType"
const initialCanceledFlightValues: ICancelFlightObject = {
  canceled: undefined,
  canceledBy: undefined,
  canceledAt: undefined,
  reason: undefined,
  description: undefined,
};

const GridContainerStyles = (theme: Theme): any => ({
  width: "100%",
  height: "100%",
  display: "flex",
  alignContent: "flex-start",
  position: "relative",
  padding: "10px",
  flexGrow: 1,
  "& .MuiGrid-item": { width: 363 },
  "& .dialog_spinner_progress": { margin: "-10px" },
  "& .field-group": {
    flexDirection: "row",
    width: 380,
  },
  "& .formik_field": {
    width: 363,
    "&.note, &.comment": { width: 736 },
  },
  "& p.required_fields_label, & .crewslot_fields_label": {
    margin: 0,
    fontWeight: 300,
    fontSize: 10,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
  },
  "& .required_fields, & .crewslot_fields": {
    backgroundColor: alpha(theme.custom.forms.labels, 0.2),
    margin: "2px 0px",
  },
  "& .flight_number": {},
  "& .day_offset, .block_duration": {
    width: "auto",
    "& .input_control": {
      "& label": { width: 80 },
      "& div.input-base": {
        maxWidth: 100,
      },
    },
  },
  "& .oc_datetime": {
    // used in flight
    alignItems: "flex-start",
  },
  "& .scheduled_out, & .scheduled_in": {
    // used in recurring flight
    width: "auto",
    alignItems: "flex-start",
    "& .input_control": {
      "& label": { width: 80 },
      "& .time-input": { width: 70 },
    },
  },
  "& .position_count": {
    width: "auto",
    "& label": { width: "auto" },
    "& .input_control": {
      "& div.input-base": {
        maxWidth: 50,
      },
    },
  },
});

type CancelFlightObjectForm = FormikProps<ICancelFlightObject>;

//////////////////////////////////
// FORM Ground Event Validation //
//////////////////////////////////
const validateCancelFlightObject = (values: ICancelFlightObject): any => {
  const { reason, description } = values;
  // nested values have to be added here as empty objects.
  const errors: any = {};

  // Only validate if the form is dirty (has changes)
  if (!reason) {
    errors.reason = "Requires reason.";
  }
  if (!description) {
    errors.description = "Requires description.";
  }
  return errors;
};

/**
 *
 * @param props -
 */
export const CancelFlightTab = (props: ICancelFlightTabProps): JSX.Element => {
  const { canceledFlight, setLoadedCanceledFlight, updateTabState, mosaicId, setAPICallStatus } = props;
  const mosaicPane = useMemo(() => document.getElementById(`${mosaicId}-panel`), [mosaicId]);

  const initialValues: ICancelFlightObject = useMemo(() => {
    const updatedCancelFlight = {
      ...canceledFlight.cancelFlight,
      canceledAt: canceledFlight.cancelFlight.canceledAt?.valueOf(),
    };
    return { ...initialCanceledFlightValues, ...updatedCancelFlight };
  }, [canceledFlight]);

  const theme = useTheme();
  const dispatch = useDispatch();

  // Structure dependencies
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { customerStructure } = structures;
  const { delayCategories } = customerStructure;

  /** GROUND EVENT CHANGE SUBMISSION */
  const handleSubmit = useCallback(
    async (values: ICancelFlightObject, helpers: FormikHelpers<ICancelFlightObject>): Promise<void> => {
      const { setSubmitting, resetForm } = helpers;
      setSubmitting(true);
      setAPICallStatus("Updating Canceled Flight");
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Updating Canceled Flight...",
        successMessage: "Canceled Flight Successfully Updated.",
        errorMessage: "Error: Unable to Update Canceled Flight",
      };

      const response = await AmplifyAPIWrapper.Put<any>(
        `/flights/canceled/${canceledFlight._id}`,
        { cancelFlight: values },
        notifications
      );
      if (response?._id) {
        setLoadedCanceledFlight(response);
        resetForm({ values: response.cancelFlight });
      }
      setSubmitting(false);
      setAPICallStatus(null);
    },
    [setAPICallStatus, dispatch, canceledFlight._id, setLoadedCanceledFlight]
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validate={validateCancelFlightObject}
    >
      {(eventForm: CancelFlightObjectForm) => {
        const { values, dirty, isValid, isSubmitting, handleReset } = eventForm;
        const blockDuration =
          canceledFlight?.actualOut && canceledFlight?.actualIn
            ? milli2DaysHrsMin(canceledFlight.actualIn?.valueOf() - canceledFlight.actualOut?.valueOf())
            : null;
        const airDuration =
          canceledFlight?.actualOff && canceledFlight?.actualOn
            ? milli2DaysHrsMin(canceledFlight.actualOn?.valueOf() - canceledFlight.actualOff?.valueOf())
            : null;
        const subtitle = blockDuration && airDuration ? `Block: ${blockDuration} / Air: ${airDuration}` : "";

        return (
          <Form style={{ width: "100%" }}>
            <FormikFormIsDirtyEffect updateParent={updateTabState} formDirtyState={dirty} />
            <CardContent id={`${mosaicId}-canceledFlight-editor`} className="form_card flight">
              <Grid container direction="column">
                <FormHeader title="Canceled Flight" subtitle={subtitle} />
                <DisplayLastModification
                  userId={canceledFlight.modifiedBy ?? canceledFlight.createdBy}
                  time={canceledFlight.modifiedAt ?? canceledFlight.createdAt}
                  removed={{
                    removalType: canceledFlight.removalType,
                    removedBy: values.canceledBy,
                    removedAt: values.canceledAt,
                  }}
                />
                <Grid container sx={GridContainerStyles(theme)} className={"form_body"}>
                  <Typography className="required_fields_label">required fields</Typography>

                  <Grid container direction="row" className="required_fields">
                    <FormikSelectField
                      className="eventTypegroup"
                      name="reason"
                      label="Reason"
                      isMulti={false}
                      options={delayCategories}
                      getOptionLabel={(type: IDelayCategory) => `${type.description}`}
                      getOptionValue={(type: IDelayCategory) => type.description}
                      placeholder="Select Diversion Reason"
                      isSearchable={true}
                      menuPortalTarget={mosaicPane}
                      menuPosition={"fixed"}
                      openMenuOnFocus={false}
                      isClearable={false}
                      format={(value: string) =>
                        delayCategories.find((option: IDelayCategory) => option.description === value)
                      }
                      parse={(option: IDelayCategory) => option.description}
                    />

                    <Grid container direction="row" className="optional_fields">
                      <FormikTextField
                        className="field textfield multiline_long"
                        label="Comments"
                        name="description"
                        multiline={true}
                        minRows={3}
                        maxRows={5}
                      />
                    </Grid>

                    <Grid container className="form_footer">
                      <Button
                        className="btn reset"
                        type="reset"
                        variant="contained"
                        color="secondary"
                        disabled={(!dirty && isValid) || isSubmitting}
                        onClick={handleReset}
                      >
                        Reset
                      </Button>
                      <Button
                        className={"btn save"}
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting || !isValid || !dirty}
                        type="submit"
                      >
                        {"Update"}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Form>
        );
      }}
    </Formik>
  );
};
